import React, { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  User, Mail, Lock, Eye, EyeOff, Camera, Upload, X,
  CheckCheck, AlertCircle, ArrowRight, CheckCircle2,
  Users, ImageIcon, MessageCircle, Sparkles, Shield
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { profileService } from '../../lib/profileService';
import ThreeBackground from '../aether/components/ThreeBackground';
/* ─── Password strength ─── */
function getStrength(pw) {
  if (!pw) return { score: 0, label: '', color: '' };
  const checks = {
    length:    pw.length >= 8,
    lowercase: /[a-z]/.test(pw),
    uppercase: /[A-Z]/.test(pw),
    number:    /\d/.test(pw),
    special:   /[!@#$%^&*(),.?":{}|<>]/.test(pw),
  };
  const score = Object.values(checks).filter(Boolean).length;
  if (score < 2) return { score, label: 'Weak',   color: '#ef4444', checks };
  if (score < 4) return { score, label: 'Fair',   color: '#f59e0b', checks };
  if (score < 5) return { score, label: 'Good',   color: '#10b981', checks };
  return           { score, label: 'Strong', color: '#6366f1', checks };
}

function StrengthBar({ password }) {
  const { score, label, color } = getStrength(password);
  if (!password) return null;
  return (
    <div className="mt-2 space-y-1.5">
      <div className="flex justify-between items-center">
        <span className="text-[10px] text-white/30">Password strength</span>
        <span className="text-[10px] font-bold" style={{ color }}>{label}</span>
      </div>
      <div className="flex gap-1">
        {[1,2,3,4,5].map(n => (
          <div key={n} className="h-1 flex-1 rounded-full transition-all duration-300"
            style={{ background: n <= score ? color : 'rgba(255,255,255,0.08)' }} />
        ))}
      </div>
    </div>
  );
}

/* ─── Avatar picker ─── */
function AvatarPicker({ value, onChange }) {
  const ref = useRef(null);
  const [preview, setPreview] = useState(null);

  const pick = (file) => {
    if (!file || !file.type.startsWith('image/')) return;
    const reader = new FileReader();
    reader.onload = (e) => { setPreview(e.target.result); onChange(file); };
    reader.readAsDataURL(file);
  };

  return (
    <div className="flex flex-col items-center gap-2 py-2">
      <div className="relative">
        {preview ? (
          <img src={preview} alt="avatar"
            className="w-24 h-24 rounded-full object-cover border-2 border-violet-500/50 shadow-lg shadow-violet-900/30" />
        ) : (
          <div className="w-24 h-24 rounded-full bg-white/5 border-2 border-dashed border-white/20 flex items-center justify-center">
            <Camera size={26} className="text-white/25" />
          </div>
        )}
        <button type="button" onClick={() => ref.current?.click()}
          className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-violet-600 hover:bg-violet-500 flex items-center justify-center shadow-lg transition-colors">
          <Upload size={14} className="text-white" />
        </button>
        {preview && (
          <button type="button" onClick={() => { setPreview(null); onChange(null); if(ref.current) ref.current.value=''; }}
            className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-rose-600 flex items-center justify-center">
            <X size={10} className="text-white" />
          </button>
        )}
      </div>
      <p className="text-[10px] text-white/25">Profile photo (optional)</p>
      <input ref={ref} type="file" accept="image/*" className="sr-only"
        onChange={e => pick(e.target.files?.[0])} />
    </div>
  );
}

/* ─── Styled field ─── */
const Field = ({ icon: Icon, label, error, children }) => (
  <div className="space-y-1.5">
    {label && (
      <label className="block text-[10px] font-bold uppercase tracking-widest text-white/40">{label}</label>
    )}
    <div className="relative">
      {Icon && <Icon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/25 pointer-events-none z-10" />}
      {children}
    </div>
    {error && (
      <p className="flex items-center gap-1 text-[11px] text-rose-400">
        <AlertCircle size={11} />{error}
      </p>
    )}
  </div>
);

const inputCls = (hasIcon) =>
  `w-full bg-white/5 border border-white/10 rounded-xl py-3 text-sm text-white placeholder-white/20 outline-none
   focus:border-violet-500/60 focus:ring-1 focus:ring-violet-500/25 transition-all
   ${hasIcon ? 'pl-9 pr-4' : 'px-4'}`;

/* ─── Success screen ─── */
function SuccessScreen({ email, onContinue }) {
  return (
    <div className="flex flex-col items-center text-center gap-6 py-8">
      <div className="w-20 h-20 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
        <CheckCircle2 size={40} className="text-emerald-400" />
      </div>
      <div>
        <h2 className="text-2xl font-bold text-white mb-2">You're in! 🎉</h2>
        <p className="text-white/40 text-sm">Account created for <span className="text-violet-300 font-medium">{email}</span></p>
      </div>
      <button onClick={onContinue}
        className="flex items-center gap-2 px-8 py-3 bg-violet-600 hover:bg-violet-500 text-white font-bold rounded-2xl shadow-lg shadow-violet-900/40 transition-all">
        Enter Aether <ArrowRight size={16} />
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════
   MAIN REGISTER PAGE
══════════════════════════════════════════ */
const Register = () => {
  const { signUp } = useAuth();
  const navigate = useNavigate();

  const [form, setForm] = useState({
    profilePhoto: null,
    full_name: '',
    username: '',
    email: '',
    password: '',
    accessCode: '',
  });
  const [errors, setErrors]       = useState({});
  const [showPw, setShowPw]       = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [success, setSuccess]     = useState(false);

  const set = (field, val) => {
    setForm(p => ({ ...p, [field]: val }));
    if (errors[field]) setErrors(p => ({ ...p, [field]: '' }));
  };

  const validate = () => {
    const e = {};
    if (form.accessCode !== '123')    e.accessCode  = 'Invalid VIP Access Code';
    if (!form.full_name.trim())       e.full_name   = 'Name is required';
    if (!form.username.trim())        e.username    = 'Username is required';
    if (!form.email.trim())           e.email       = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email';
    if (form.password.length < 6)     e.password    = 'Minimum 6 characters';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const quickAdmin = async () => {
    setIsLoading(true);
    try {
      // Create admin mock account if needed and login
      await signUp({ email: 'admin@freezone.com', password: 'adminpassword', options: { data: { full_name: 'Admin User', username: 'admin' } } });
      navigate('/admin');
    } catch { 
      navigate('/admin'); 
    } finally { 
      setIsLoading(false); 
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsLoading(true);
    try {
      const { data, error } = await signUp({
        email: form.email,
        password: form.password,
        options: { data: { full_name: form.full_name, username: form.username } },
      });
      if (error) throw error;
      if (data?.user) {
        let avatarUrl = null;
        if (form.profilePhoto) {
          try { avatarUrl = await profileService.uploadAvatar(data.user.id, form.profilePhoto); } catch (e) {}
        }
        await profileService.updateProfile(data.user.id, { 
          fullName: form.full_name, 
          username: form.username,
          avatarUrl: avatarUrl 
        });
      }
      setSuccess(true);
    } catch (err) {
      setErrors({ submit: err?.message || 'Registration failed. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Join Aether — Create Your Account</title>
        <meta name="description" content="Create your Aether account and start connecting." />
      </Helmet>

      <div className="min-h-screen bg-background text-white relative flex items-center justify-center p-4 overflow-hidden">
        <ThreeBackground />

        {/* Glows */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 rounded-full bg-violet-600/10 blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 rounded-full bg-indigo-600/10 blur-3xl" />
        </div>

        <div className="relative z-10 w-full max-w-md my-auto h-[90vh] overflow-y-auto no-scrollbar pb-10">

          {/* Logo + heading */}
          <div className="text-center mb-8 mt-10">
            <div className="flex justify-center mb-5">
              <div className="w-16 h-16 rounded-2xl bg-violet-600/20 border border-violet-500/30 flex items-center justify-center shadow-lg shadow-violet-900/30 backdrop-blur-sm">
                <Sparkles size={28} className="text-violet-300" />
              </div>
            </div>
            <h1 className="text-3xl font-bold text-white mb-2">
              Create Account
            </h1>
            <p className="text-white/40 text-sm">Sign up to get started</p>
          </div>

          {/* Card */}
          <div className="border border-white/10 rounded-[28px] shadow-2xl overflow-hidden"
            style={{ background: 'rgba(15,10,30,0.75)', backdropFilter: 'blur(24px)' }}>

            {/* Card header bar */}
            <div className="px-6 pt-6 pb-4 border-b border-white/8">
              <p className="text-xs font-bold uppercase tracking-widest text-white/30">
                {success ? 'Account Created' : 'New Account'}
              </p>
            </div>

            <div className="p-6">
              {success ? (
                <SuccessScreen email={form.email} onContinue={() => navigate('/news-feed')} />
              ) : (
                <form onSubmit={handleSubmit} className="space-y-5">

                  {/* Profile Photo */}
                  <AvatarPicker value={form.profilePhoto} onChange={f => set('profilePhoto', f)} />

                  {/* VIP Access Code */}
                  <Field icon={Shield} label="Curator Access Code" error={errors.accessCode}>
                    <input type="text" placeholder="Enter VIP Code (e.g. 123)"
                      value={form.accessCode} onChange={e => set('accessCode', e.target.value)}
                      className={inputCls(true)} />
                  </Field>

                  {/* Name + Username Row */}
                  <div className="grid grid-cols-2 gap-4">
                    <Field icon={User} label="Full Name" error={errors.full_name}>
                      <input type="text" placeholder="Alex"
                        value={form.full_name} onChange={e => set('full_name', e.target.value)}
                        className={inputCls(true)} />
                    </Field>
                    <Field icon={User} label="Username" error={errors.username}>
                      <input type="text" placeholder="alex123"
                        value={form.username} onChange={e => set('username', e.target.value)}
                        className={inputCls(true)} />
                    </Field>
                  </div>

                  {/* Email */}
                  <Field icon={Mail} label="Email Address" error={errors.email}>
                    <input type="email" placeholder="you@example.com"
                      value={form.email} onChange={e => set('email', e.target.value)}
                      className={inputCls(true)} />
                  </Field>

                  {/* Password */}
                  <Field icon={Lock} label="Password" error={errors.password}>
                    <input type={showPw ? 'text' : 'password'} placeholder="Min. 6 characters"
                      value={form.password} onChange={e => set('password', e.target.value)}
                      className={`${inputCls(true)} pr-10`} />
                    <button type="button" onClick={() => setShowPw(!showPw)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60 transition-colors">
                      {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                  </Field>
                  <StrengthBar password={form.password} />

                  {/* Terms Text Only */}
                  <p className="text-xs text-white/50 text-center leading-relaxed">
                    By registering, you agree to the{' '}
                    <span className="text-violet-400 font-semibold">Terms</span> and{' '}
                    <span className="text-violet-400 font-semibold">Privacy Policy</span>.
                  </p>

                  {/* Submit error */}
                  {errors.submit && (
                    <div className="flex items-center gap-2 px-4 py-3 rounded-xl bg-rose-500/10 border border-rose-500/30 text-rose-300 text-sm">
                      <AlertCircle size={14} className="flex-shrink-0" />{errors.submit}
                    </div>
                  )}

                  {/* Submit button */}
                  <button type="submit" disabled={isLoading}
                    className="w-full flex items-center justify-center gap-2 py-3.5 rounded-2xl bg-violet-600 hover:bg-violet-500 text-white font-bold text-sm shadow-lg shadow-violet-900/40 transition-all disabled:opacity-60">
                    {isLoading ? (
                      <><span className="w-4 h-4 rounded-full border-2 border-white/30 border-t-white animate-spin" /> Creating Account…</>
                    ) : (
                      <>Get Started <ArrowRight size={16} /></>
                    )}
                  </button>

                  {/* Sign in link */}
                  <p className="text-center text-sm text-white/30 pt-1 pb-2">
                    Already have an account?{' '}
                    <Link to="/login" className="text-violet-400 hover:text-violet-300 font-bold transition-colors">
                      Sign In
                    </Link>
                  </p>

                  {/* Quick Admin Access */}
                  <div className="pt-3 border-t border-white/8">
                    <button type="button" onClick={quickAdmin} disabled={isLoading}
                      className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl border border-rose-500/20 bg-rose-500/5 hover:bg-rose-500/10 text-rose-400 font-bold text-sm transition-all">
                      <Shield size={14} /> Quick Admin Access
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>

          {/* Feature pills — only on non-success */}
          {!success && (
            <div className="mt-6 grid grid-cols-3 gap-3">
              {[
                { icon: Users, label: 'Connect Friends', color: 'text-violet-400', bg: 'bg-violet-500/10 border-violet-500/20' },
                { icon: ImageIcon, label: 'Share Moments', color: 'text-indigo-400', bg: 'bg-indigo-500/10 border-indigo-500/20' },
                { icon: MessageCircle, label: 'Stay Connected', color: 'text-pink-400', bg: 'bg-pink-500/10 border-pink-500/20' },
              ].map(({ icon: Icon, label, color, bg }) => (
                <div key={label} className={`flex flex-col items-center gap-2 py-3 px-2 rounded-2xl border ${bg}`}>
                  <Icon size={18} className={color} />
                  <span className="text-[10px] text-white/40 text-center leading-tight">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Register;